import React, { PropsWithChildren } from "react"
import { ApolloClient, InMemoryCache, ApolloLink, defaultDataIdFromObject } from "@apollo/client"
import { ApolloProvider } from "@apollo/client/react"
import UploadHttpLink from "apollo-upload-client/UploadHttpLink.mjs"
import { BatchHttpLink } from "@apollo/client/link/batch-http"
import { SetContextLink } from "@apollo/client/link/context"
import { LocalState } from "@apollo/client/local-state"
import { Auth0ContextInterface, useAuth0 } from "@auth0/auth0-react"
import { map } from "rxjs/operators"
import { loadEnvVars } from "../utils/loadEnvVars"
import { getAccessTokenSilentlyWithLock, handleAuth0RecoverableError } from "../utils/auth0Errors"

type GetAccessTokenSilentlyType = Pick<Auth0ContextInterface, "getAccessTokenSilently">["getAccessTokenSilently"]
type LoginWithRedirectType = Pick<Auth0ContextInterface, "loginWithRedirect">["loginWithRedirect"]
type AuthorizedApolloProviderProps = PropsWithChildren<{
  client?: (
    getAccessTokenSilently: GetAccessTokenSilentlyType,
    loginWithRedirect: LoginWithRedirectType
  ) => ApolloClient
}>

const asyncAuthLink = (getAccessTokenSilently: GetAccessTokenSilentlyType, loginWithRedirect: LoginWithRedirectType) =>
  new SetContextLink(async (prevContext, operation) => {
    let token = ""
    try {
      token = await getAccessTokenSilentlyWithLock(getAccessTokenSilently)
    } catch (tokenError) {
      await handleAuth0RecoverableError(tokenError, loginWithRedirect)
      throw tokenError
    }

    return {
      headers: {
        ...prevContext.headers,
        Authorization: `Bearer ${token}`,
        "apollo-require-preflight": "true",
        "x-apollo-operation-name": operation.operationName || "UnknownOp"
      }
    }
  })

const requestLoggerLink = new ApolloLink((operation, forward) => {
  if (!forward) {
    throw new Error("No forward link provided")
  }

  const operationName = operation.operationName || "UnknownOp"
  const operationType = operation.query.definitions.find(d => d.kind === "OperationDefinition") as
    | { operation?: "query" | "mutation" | "subscription" }
    | undefined
  const type = operationType?.operation ?? "operation"
  const startedAt = performance.now()
  const request = {
    operationName,
    type,
    variables: operation.variables,
    context: operation.getContext()
  }

  return forward(operation).pipe(
    map(result => {
      const elapsed = Math.round(performance.now() - startedAt)
      const baseColor =
        type === "query" ? "#2563eb" : type === "mutation" ? "#7c3aed" : type === "subscription" ? "#0d9488" : "#374151"
      const color = result.errors?.length ? "#dc2626" : baseColor

      console.groupCollapsed(
        `%c${type.toUpperCase()}%c %c${operationName}%c %c${elapsed}ms`,
        `color:${color};font-weight:700`,
        "color:inherit;font-weight:400",
        "color:inherit;font-weight:700",
        "color:inherit;font-weight:400",
        "color:#6b7280;font-weight:500"
      )
      console.log({
        request,
        result: {
          data: result.data,
          errors: result.errors
        }
      })
      console.groupEnd()
      return result
    })
  )
})

const hasActualFile = (v: unknown): boolean => {
  if (!v) return false
  if (typeof File !== "undefined" && v instanceof File) return true
  if (typeof Blob !== "undefined" && v instanceof Blob) return true
  if (Array.isArray(v)) return v.some(hasActualFile)
  if (typeof v === "object") return Object.values(v as Record<string, unknown>).some(hasActualFile)
  return false
}

const defaultApolloClient = (
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  loginWithRedirect: LoginWithRedirectType
) => {
  const { graphqlApiGateway } = loadEnvVars()
  const uri = `${graphqlApiGateway}`

  const httpLink = ApolloLink.split(
    operation => hasActualFile(operation.variables),
    new UploadHttpLink({
      uri,
      headers: {
        "keep-alive": "true"
      },
      credentials: "include"
    }) as unknown as ApolloLink,
    new BatchHttpLink({ uri, credentials: "include", batchMax: 10, batchInterval: 20 })
  )

  const PREFERRED_KEYS = ["sfid", "uuid", "id", "_id", "user_id", "now", "dossier_sfid"]

  const cache = new InMemoryCache({
    dataIdFromObject(object) {
      // Let Apollo handle the usual cases (id/_id) first
      // NOTES: You can omit Query field policies unless you need pagination.
      // By default Apollo keys field results by *all arguments* and replaces arrays.

      const def = defaultDataIdFromObject(object)
      if (def) return def

      // Generic: use the first id-like field you find
      for (const k of PREFERRED_KEYS) {
        const v = object[k]
        if (v != null) return `${object.__typename}:${v}`
      }

      // No stable id → leave unnormalized (falls back to by-reference)
      return undefined
    }
  })

  return new ApolloClient({
    link: ApolloLink.from([asyncAuthLink(getAccessTokenSilently, loginWithRedirect), requestLoggerLink, httpLink]),
    cache,
    localState: new LocalState({
      resolvers: {
        profiles: {
          cache: () => false
        }
      }
    })
  })
}

const AuthorizedApolloProvider: React.FunctionComponent<AuthorizedApolloProviderProps> = ({
  children,
  client = defaultApolloClient
}) => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0()
  const authorizedApolloClient = React.useMemo(
    () => client(getAccessTokenSilently, loginWithRedirect),
    [client, getAccessTokenSilently, loginWithRedirect]
  )

  return <ApolloProvider client={authorizedApolloClient}>{children}</ApolloProvider>
}

export default AuthorizedApolloProvider
